import { isEmpty } from 'validate.js';
import { SharesConversionWritable } from 'api';
import { CashDistributionLedgerAcquisition, ShareSaleReport } from 'pages/CapTable/fund-ownership/components/cash-distribution/CashDistributionLedger.props';
import { AcquisitionCell } from 'pages/CapTable/fund-ownership/models/fund-ownership.models';
import slashedToIsoFormat from 'utilities/slashedToIsoFormat';
import { AMOUNT_ALIAS, CONVERSION_DATE_ALIAS, COST_BASIS_ALIAS, PURCHASE_DATE_ALIAS, SALE_DATE_ALIAS, SOLD_PERCENT_ALIAS } from '../constants';

export interface PurchasedReverseParserParams {
  purchasedCells: Record<string, AcquisitionCell>;
  isConvertibleNote?: boolean;
}

export interface SoldReverseParserParams {
  soldCells: Record<string, AcquisitionCell>;
  acquisitions: Array<Partial<CashDistributionLedgerAcquisition>>;
}

export interface ConvertedReverseParserParams {
  convertedCells: Record<string, AcquisitionCell>;
  acquisitions: Array<Partial<CashDistributionLedgerAcquisition>>;
}

export interface CommonReverseParserParams {
  purchasedCells: Record<string, AcquisitionCell>;
  soldCells: Record<string, AcquisitionCell>;
  convertedCells: Record<string, AcquisitionCell>;
  isConvertibleNote: boolean;
}


export const purchasedReverseParser = ({ purchasedCells, isConvertibleNote = false }: PurchasedReverseParserParams) => {
  // group purchasedCells by acquisition_ref
  const groupedPurchasedCellsMap: Record<string, Array<AcquisitionCell>> = Object.values(purchasedCells).reduce((acc, cell) => {
    acc[cell.acquisition_ref] = acc[cell.acquisition_ref] ? [...acc[cell.acquisition_ref], cell] : [cell];
    return acc;
  }, {} as Record<string, Array<AcquisitionCell>>);

  const acquisitions = Object.values(groupedPurchasedCellsMap).map(cells => {
    // any is used because the dynamic nature of the alias
    const acquisition: any = {
      sales: [],
    }

    cells.forEach(cell => {
      acquisition[cell.alias]
        = cell.alias === PURCHASE_DATE_ALIAS && cell.value ? slashedToIsoFormat({ label: cell.value as string }) : cell.value;
    });
    
    // For convertible notes: use amount, set cost_basis to null
    // For all other security types: use cost_basis, set amount to null
    if (isConvertibleNote) {
      acquisition[COST_BASIS_ALIAS] = null;
    } else {
      acquisition[AMOUNT_ALIAS] = null;
    }
    
    return acquisition;
  });
  return acquisitions;
};

export const soldReverseParser = ({ soldCells, acquisitions }: SoldReverseParserParams): Array<CashDistributionLedgerAcquisition> => {
  const updatedAcquisitions = [...acquisitions];
  // add sales to acquisitions
  return updatedAcquisitions.map(acquisition => {
    const relevantCells = Object.values(soldCells).filter(cell => cell.acquisition_ref === acquisition.acquisition_ref);
    // group cells into an object by their row number
    const groupedCells: Record<number, Array<AcquisitionCell>> = relevantCells.reduce((acc, cell) => {
      acc[cell.rowNumber!] = acc[cell.rowNumber!] ? [...acc[cell.rowNumber!], cell] : [cell];
      return acc;
    }, {} as Record<number, Array<AcquisitionCell>>);
    const updatedSales = Object.values(groupedCells).map(cells => {
      const sale: any = {}
      cells.forEach(cell => {
        if (cell.alias === SOLD_PERCENT_ALIAS && cell.value) {
          sale[cell.alias] = Number(cell.value).toFixed(2);
        } else if (cell.alias === SALE_DATE_ALIAS && cell.value) {
          sale[cell.alias] = slashedToIsoFormat({ label: cell.value as string });
        } else {
          sale[cell.alias] = cell.value;
        }
      });
      return sale as ShareSaleReport;
    });
    return { ...acquisition as CashDistributionLedgerAcquisition, sales: updatedSales };
  });
};

export const convertedReverseParser = ({ convertedCells, acquisitions }: ConvertedReverseParserParams): Array<CashDistributionLedgerAcquisition> => {
  const updatedAcquisitions = [...acquisitions];
  // add conversions to acquisitions
  return updatedAcquisitions.map(acquisition => {
    const relevantCells = Object.values(convertedCells).filter(
      cell => cell.acquisition_ref === acquisition.acquisition_ref
    );
    // group cells into an object by their row number
    const groupedCells: Record<number, Array<AcquisitionCell>> = relevantCells.reduce((acc, cell) => {
      acc[cell.rowNumber!] = acc[cell.rowNumber!] ? [...acc[cell.rowNumber!], cell] : [cell];
      return acc;
    }, {} as Record<number, Array<AcquisitionCell>>);
    const updatedConversions = Object.values(groupedCells).map(cells => {
      const conversion: any = {};
      cells.forEach(cell => {
        conversion[cell.alias]
          = cell.alias === CONVERSION_DATE_ALIAS && cell.value ? slashedToIsoFormat({ label: cell.value as string }) : cell.value;
      });
      return conversion as SharesConversionWritable;
    });
    return { ...acquisition as CashDistributionLedgerAcquisition, conversions: updatedConversions };
  });
};

export const commonReverseParser = ({ purchasedCells, soldCells, convertedCells, isConvertibleNote = false }: CommonReverseParserParams): Array<CashDistributionLedgerAcquisition> => {
  if (isEmpty(purchasedCells) && isEmpty(soldCells) && isEmpty(convertedCells)) return [];
  const acquisitions = purchasedReverseParser({ purchasedCells, isConvertibleNote });
  if (isEmpty(soldCells) && isEmpty(convertedCells)) return acquisitions;

  const acquisitionsPlusSales = soldReverseParser({ soldCells, acquisitions });
  if (isEmpty(convertedCells)) {
    return acquisitionsPlusSales;
  }
  return convertedReverseParser({ convertedCells, acquisitions: acquisitionsPlusSales });
};
